import { AdminCohorts } from '@platform/client';
import {
  DatePicker,
  FormsCheckBox,
  FormsInputDropdown,
  InlineFormInput,
  InputDropdownItem,
} from '@platform/components';
import { Control, Controller, FormState, UseFormRegister } from 'react-hook-form';

export type CohortFormData = {
  name: string;
  clientId: string;
  available: boolean;
  kickoffDate?: Date | null;
  startDate?: Date | null;
  endDate?: Date | null;
  applicationStartDate?: Date | null;
  applicationEndDate?: Date | null;
  url?: string | null;
  description?: string | null;
  template_id?: string | null;
  duration?: string | null;
  timeCommitment?: string | null;
};

type CohortFormProps = {
  control: Control<CohortFormData>;
  formState: FormState<CohortFormData>;
  register: UseFormRegister<CohortFormData>;
};

export const CohortForm = (props: CohortFormProps) => {
  const { control, formState, register } = props;
  const { data: clientData } = AdminCohorts.useGetCohortClients();
  const { data: templateData } = AdminCohorts.useGetCohortTemplates();
  const cohortClients = clientData?.clients || [];
  const cohortTemplates = templateData?.templates || [];

  const clientsDropdownItems: InputDropdownItem[] = cohortClients.map((client) => ({
    text: client.name,
    textValue: client.id,
  }));

  const templateDropdownItems: InputDropdownItem[] = cohortTemplates.map((template) => ({
    text: template.cohortType,
    textValue: template.id,
  }));

  return (
    <form className='flex flex-col gap-4 min-w-[800px]'>
      <InlineFormInput
        register={() => register('name', { required: 'Name is required' })}
        label='Name'
        errors={formState.errors.name}
        customWidth='w-full'
      />
      <FormsCheckBox size='sm' control={control} name='available' description='Available' />
      <InlineFormInput
        register={() =>
          register('description', {
            required: 'Description is required',
            maxLength: { value: 95, message: 'Description cannot exceed 95 characters' },
          })
        }
        label='Description'
        errors={formState.errors.description}
        customWidth='w-full'
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='clientId'
        label='Client'
        items={clientsDropdownItems}
        rules={{ required: 'Client selection is required' }}
        error={formState.errors.clientId}
      />
      <FormsInputDropdown
        type='default'
        control={control}
        name='template_id'
        label='Template'
        items={templateDropdownItems}
      />
      <InlineFormInput
        register={() => register('duration')}
        label='Duration'
        errors={formState.errors.duration}
        customWidth='w-full'
      />
      <InlineFormInput
        register={() => register('timeCommitment')}
        label='Time Commitment'
        errors={formState.errors.timeCommitment}
        customWidth='w-full'
      />
      <InlineFormInput
        prefixContent='https://'
        register={() => register('url')}
        label='Url'
        errors={formState.errors.url}
        customWidth='w-full'
      />
      <CohortDates control={control} formState={formState} />
      <ApplicationPeriod control={control} />
    </form>
  );
};

const CohortDates = (props: {
  control: Control<CohortFormData>;
  formState: FormState<CohortFormData>;
}) => {
  const { control } = props;
  return (
    <div>
      <p className='mb-2 text-sm text-gray-500 font-medium'>Cohort dates</p>
      <div className='md:flex gap-10'>
        <Controller
          name='kickoffDate'
          control={control}
          render={({ field }) => (
            <DatePicker
              label='Kickoff'
              initialDate={field.value ?? undefined}
              onChange={(newDate) => field.onChange(newDate)}
            />
          )}
        />
        <Controller
          name='startDate'
          control={control}
          render={({ field }) => (
            <DatePicker
              label='Start'
              initialDate={field.value ?? undefined}
              onChange={(newDate) => field.onChange(newDate)}
            />
          )}
        />
        <Controller
          name='endDate'
          control={control}
          render={({ field }) => (
            <DatePicker
              label='End'
              initialDate={field.value ?? undefined}
              onChange={(newDate) => field.onChange(newDate)}
            />
          )}
        />
      </div>
    </div>
  );
};

const ApplicationPeriod = (props: { control: Control<CohortFormData> }) => {
  const { control } = props;
  return (
    <div>
      <p className='mb-2 text-sm text-gray-500 font-medium'>Application Period</p>
      <div className='md:flex gap-10'>
        <Controller
          name='applicationStartDate'
          control={control}
          render={({ field }) => (
            <DatePicker
              label='Start'
              initialDate={field.value ?? undefined}
              onChange={(newDate) => field.onChange(newDate)}
            />
          )}
        />
        <Controller
          name='applicationEndDate'
          control={control}
          render={({ field }) => (
            <DatePicker
              label='End'
              initialDate={field.value ?? undefined}
              onChange={(newDate) => field.onChange(newDate)}
            />
          )}
        />
      </div>
    </div>
  );
};
